import { safeMathRandom } from '@/utils/number.js'
import { Model, Factory, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randProductName, randNumber } from '@ngneat/falso'

import type { SchemaAttributes } from '../../../types.js'
import type { MirageServer, MockServer } from '../../server.js'
import {
  filterable,
  pageable,
  sortable,
  createErrorResponse,
} from '../../utils.js'

// Use schema types - the component is named 'ItemList' in the OpenAPI schema
// (CatalogItemList is only the JSON:API type field value)
type CatalogItemListData = SchemaAttributes<'ItemList'>

export const model = Model as ModelDefinition<CatalogItemListData>

const sampleDepartmentNames = [
  '0. Central Department',
  'Bristol',
  'London',
  'Brighton',
  'Cardiff',
  'Manchester',
]

export const factory = Factory.extend<CatalogItemListData>({
  name() {
    return randProductName() + ' Catalogue'
  },
  central() {
    return false
  },
  items_count() {
    return randNumber({ min: 0, max: 500 })
  },
  departments_count() {
    return randNumber({ min: 0, max: 5 })
  },
  departments() {
    const count = randNumber({ min: 0, max: 3 })
    const shuffled = [...sampleDepartmentNames].sort(
      () => safeMathRandom() - 0.5,
    )
    return shuffled.slice(0, count)
  },
})

export function routes(server: MockServer) {
  // GET /catalog/management/item-lists/v1 - List with pagination and filters
  server.get(
    '/catalog/management/item-lists/v1',
    function (this: MirageServer, schema, request) {
      const itemLists = schema.all('catalogItemList')
      const filtered = filterable(itemLists, request, {
        name: (item, value) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        central: (item, value) => String(item.central) === value,
        department: (item, value) => {
          // Filter by department - show item lists that have this department
          const departments = item.departments || []
          // Look up department name by ID
          const dept = schema.find('department', value)
          const deptName = dept?.attrs?.name || ''
          return departments.includes(deptName)
        },
        department_groups: (item, value) => {
          // Filter by department groups - show item lists that have departments in these groups
          if (!value) {
            return true
          }
          const groupIds = String(value)
            .split(',')
            .map((id: string) => id.trim())
            .filter(Boolean)
          if (groupIds.length === 0) {
            return true
          }
          const departments = (item.departments || []) as string[]
          // Resolve department names to their group IDs via Mirage schema
          return departments.some((deptName: string) => {
            const allDepts = schema.all('department')
            const dept = allDepts.models.find((d) => d.attrs.name === deptName)
            return (
              dept?.attrs.group_id &&
              groupIds.includes(String(dept.attrs.group_id))
            )
          })
        },
      })
      const sorted = sortable(filtered, request)
      const { data, meta } = pageable(sorted, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  // POST /catalog/management/item-lists/v1 - Create new item list
  server.post(
    '/catalog/management/item-lists/v1',
    function (this: MirageServer, schema, request) {
      const body = JSON.parse(request.requestBody)
      const attrs = body.data?.attributes ?? {}

      if (!attrs.name || attrs.name.trim() === '') {
        return createErrorResponse({
          status: 422,
          errors: [
            {
              detail: 'Name is required',
              pointer: '/data/attributes/name',
            },
          ],
        })
      }

      const itemList = schema.create('catalogItemList', {
        name: attrs.name.trim(),
        central: false,
        items_count: 0,
        departments_count: 0,
        departments: [],
      })

      return itemList
    },
  )

  // GET /catalog/management/item-lists/v1/:id - Get single item list
  server.get(
    '/catalog/management/item-lists/v1/:id',
    function (this: MirageServer, schema, request) {
      const itemList = schema.find('catalogItemList', request.params.id)
      if (!itemList) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Item list not found' }],
        })
      }
      return itemList
    },
  )

  // PATCH /catalog/management/item-lists/v1/:id - Update item list
  server.patch('/catalog/management/item-lists/v1/:id', (schema, request) => {
    const itemList = schema.find('catalogItemList', request.params.id)
    if (!itemList) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Item list not found' }],
      })
    }

    const body = JSON.parse(request.requestBody)
    const attrs = body.data?.attributes ?? {}

    if (attrs.name !== undefined && attrs.name.trim() === '') {
      return createErrorResponse({
        status: 422,
        errors: [
          {
            detail: 'Name cannot be empty',
            pointer: '/data/attributes/name',
          },
        ],
      })
    }

    itemList.update({
      name: attrs.name?.trim() ?? itemList.attrs.name,
    })

    return {
      data: {
        id: itemList.id,
        type: 'CatalogItemList',
        attributes: {
          name: itemList.attrs.name,
          central: itemList.attrs.central,
          items_count: itemList.attrs.items_count,
          departments_count: itemList.attrs.departments_count,
          departments: itemList.attrs.departments,
        },
      },
    }
  })

  // DELETE /catalog/management/item-lists/v1/:id - Delete item list
  server.delete(
    '/catalog/management/item-lists/v1/:id',
    function (this: MirageServer, schema, request) {
      const itemList = schema.find('catalogItemList', request.params.id)
      if (!itemList) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Item list not found' }],
        })
      }

      // Check if list contains any items
      if (itemList.items_count > 0) {
        return createErrorResponse({
          status: 422,
          errors: [
            {
              detail: 'Item list contains items and cannot be deleted',
              pointer: '/data/relationships/items',
            },
          ],
        })
      }

      itemList.destroy()
      return new Response(204)
    },
  )
}

export function seeds(server: MockServer) {
  // Create sample item lists matching legacy data
  server.create('catalogItemList', {
    id: '1',
    name: 'Default',
    central: false,
    items_count: 1,
    departments_count: 0,
    departments: [],
  })
  server.create('catalogItemList', {
    id: '2',
    name: 'Central Item List',
    central: true,
    items_count: 8,
    departments_count: 1,
    departments: ['0. Central Department'],
  })
  server.create('catalogItemList', {
    id: '3',
    name: 'Bristol Item List',
    central: false,
    items_count: 8,
    departments_count: 1,
    departments: ['Bristol'],
  })
  server.create('catalogItemList', {
    id: '4',
    name: 'London Item List',
    central: false,
    items_count: 8,
    departments_count: 1,
    departments: ['London'],
  })
  server.create('catalogItemList', {
    id: '5',
    name: 'Brighton Item List',
    central: false,
    items_count: 8,
    departments_count: 1,
    departments: ['Brighton'],
  })
  server.create('catalogItemList', {
    id: '6',
    name: 'Cardiff Item List',
    central: false,
    items_count: 8,
    departments_count: 1,
    departments: ['Cardiff'],
  })
}
